import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, formatApiError, PLATFORMS } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ClipCard } from "@/components/ClipCard";
import { TrailerStudio } from "@/components/TrailerStudio";
import { ClipPushToggle } from "@/components/ClipPushToggle";
import { useAuth } from "@/context/AuthContext";

export default function ClipsLibrary() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [clips, setClips] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/clips");
    setClips(data);
    setLoaded(true);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rendering = clips.some((c) => c.status === "rendering");
  useEffect(() => {
    const i = setInterval(() => { if (!document.hidden) load(); }, rendering ? 3000 : 5000);
    return () => clearInterval(i);
  }, [rendering, load]);

  const del = async (c) => {
    if (!window.confirm(t("cl.confirm_delete"))) return;
    try { await api.delete(`/clips/${c.id}`); toast.success(t("cl.deleted")); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const rerender = async (c) => { await api.post(`/clips/${c.id}/rerender`); toast.success(t("cl.rerendered")); load(); };

  const [sel, setSel] = useState(() => new Set());
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [busy, setBusy] = useState(false);
  const bulkDelete = async () => {
    if (!window.confirm(t("cl.bulk_confirm", { n: sel.size }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/clips/bulk-delete", { ids: [...sel] });
      toast.success(t("cl.bulk_done", { n: data.deleted }));
      setSel(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const shown = filter === "all" ? clips : clips.filter((c) => c.platform === filter);
  const allSel = shown.length > 0 && shown.every((c) => sel.has(c.id));
  const selectAll = () => setSel(allSel ? new Set() : new Set(shown.map((c) => c.id)));
  useEffect(() => { setSel((s) => { const ids = new Set(clips.map((c) => c.id)); const n = new Set([...s].filter((x) => ids.has(x))); return n.size === s.size ? s : n; }); }, [clips]);

  return (
    <div className="px-4 sm:px-6 lg:px-10 2xl:px-14 py-6 sm:py-10">
      <div className="cx-label cx-rise">{t("cl.label")}</div>
      <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl mt-3 cx-rise"><span className="cx-chrome">{t("cl.title1")}</span><span className="cx-text-grad">{t("cl.title2")}</span></h1>
      <p className="text-slate-400 mt-3 text-sm sm:text-base">{t("cl.sub")}</p>
      <TrailerStudio demo={user?.role === "demo"} />
      <div className="mt-4"><ClipPushToggle /></div>
      <div className="flex gap-2 mt-8 overflow-x-auto cx-scroll pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="clip-platform-filter">
        {[{ id: "all", label: t("cl.all") }, ...PLATFORMS].map((p) => (
          <button key={p.id} data-testid={`clip-filter-${p.id}`} onClick={() => setFilter(p.id)}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium border transition-colors ${filter === p.id ? "cx-btn border-transparent" : "border-white/10 text-slate-400 hover:text-white"}`}>
            {p.label} <span className="opacity-60 ml-1">{p.id === "all" ? clips.length : clips.filter((c) => c.platform === p.id).length}</span>
          </button>
        ))}
      </div>
      {shown.length > 0 && (
        <div className={`flex flex-wrap items-center gap-3 mt-4 rounded-xl px-4 py-3 border transition-colors ${sel.size ? "sticky top-2 z-20 bg-[#140a1f]/95 backdrop-blur-md border-[#ff4fd8]/40 shadow-[0_0_24px_-8px_rgba(255,79,216,0.7)]" : "border-white/8 bg-black/20"}`} data-testid="clips-bulk-bar">
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input type="checkbox" checked={allSel} onChange={selectAll} data-testid="clips-select-all" className="w-4 h-4 accent-[#ff4fd8]" />
            {t("cl.select_all")}
          </label>
          <span className="text-xs font-mono text-[#ff9df0]" data-testid="clips-selected-count">{t("cl.selected", { n: sel.size })}</span>
          {sel.size > 0 && (
            <>
              <button onClick={() => setSel(new Set())} data-testid="clips-clear-selection" className="cx-ghost rounded-full px-3 py-1.5 text-xs">{t("cl.clear_sel")}</button>
              <button onClick={bulkDelete} disabled={busy} data-testid="clips-bulk-delete-btn" className="ml-auto rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5 bg-rose-500/90 hover:bg-rose-500 text-white disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" />{t("cl.bulk_delete", { n: sel.size })}
              </button>
            </>
          )}
        </div>
      )}
      {loaded && shown.length === 0 ? (
        <div className="cx-card rounded-xl p-10 sm:p-12 text-center text-slate-400 mt-8" data-testid="clips-empty">{t("cl.empty")}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 min-[1900px]:grid-cols-5 gap-5 mt-6" data-testid="clips-library-grid">
          {shown.map((c, i) => <ClipCard key={c.id} c={c} index={i} onDelete={del} onRerender={rerender} selected={sel.has(c.id)} onSelect={toggle} />)}
        </div>
      )}
    </div>
  );
}
